/* eslint-disable @typescript-eslint/member-ordering */

import { logger } from '@accedo/logger';
import { PlaybackService } from '##/services/elevate/types';
import {
  ContinueWatchingShowResponse,
  PlaybackActionHelperProps,
  PlaybackActionParams,
  PlaybackResponse,
  PlayPosition,
} from '##/dataModels/playback';
import { getDeviceInfo } from '##/utils/deviceUtils';
import { APP_NAME, APP_VERSION } from '##/env';
import { CacheService } from '##/services/cache';
import { MediaItem } from '##/dataModels/mediaItem';
import { getNZOSBaseUrl } from '##/services/nzos/config';

const UNKNOWN = 'Unknown';

// Logger prefix
const LP = '[NzosPlaybackService]';

// Cache TTL constants
const SHORT_TTL = 600; // 10 minutes

const playbackCache = CacheService.createCache({
  cacheType: 'localStorage',
  prefix: 'nzos-playback',
  ttlSeconds: SHORT_TTL,
  automaticCleanup: true,
  flushOnStart: true,
});

/**
 * NZOS Playback Service.
 * Handles video playback sessions and continue watching functionality.
 */
class NzosPlaybackService implements PlaybackService {
  // eslint-disable-next-line no-use-before-define
  private static instance: NzosPlaybackService;
  sessionId: string | undefined;

  private constructor() {
    logger.debug(`${LP} Creating new instance of NzosPlaybackService`);
  }

  /**
   * Get singleton instance.
   * @returns NzosPlaybackService instance.
   */
  static getInstance(): NzosPlaybackService {
    if (!NzosPlaybackService.instance) {
      NzosPlaybackService.instance = new NzosPlaybackService();
    }
    return NzosPlaybackService.instance;
  }

  /**
   * Make authenticated API call to NZOS.
   * @param endpoint - API endpoint.
   * @param token - Auth token.
   * @param body - Request body.
   * @param method - HTTP method.
   * @param queryParams - Query parameters for GET requests.
   * @returns Response data.
   */
  private static async apiCall<T>(
    endpoint: string,
    token: string,
    body: unknown = undefined,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    queryParams: Record<string, string | number | boolean> = {},
  ): Promise<T> {
    const baseUrl = getNZOSBaseUrl();
    let url = `${baseUrl}${endpoint}`;

    // Add query parameters for GET requests
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        params.append(key, String(value));
      });
      url = `${url}?${params.toString()}`;
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    // Only add Content-Type for methods that can have a body
    if (method !== 'GET' && method !== 'DELETE') {
      headers['Content-Type'] = 'application/json';
    }

    const options: RequestInit = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    logger.debug(`${LP} API call: ${method} ${url}`);

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`${LP} API error: ${response.status} ${errorText}`);
      throw new Error(`NZOS API error: ${response.status} ${errorText}`);
    }

    // Handle empty responses (e.g., 204 No Content)
    const text = await response.text();
    return text ? JSON.parse(text) : ({} as T);
  }

  /**
   * Get continue watching data for user.
   * @param params - Parameters.
   * @param params.token - Auth token.
   * @param params.clearCache - Clear cache flag.
   * @returns List of continue watching items (MediaItems with watchingProgress).
   */
  // eslint-disable-next-line class-methods-use-this
  async getContinueWatchingData({
    token,
    clearCache = false,
  }: {
    token: string;
    clearCache?: boolean;
  }): Promise<MediaItem[]> {
    logger.debug(
      `${LP} getContinueWatchingData called - clearCache:`,
      clearCache,
      'timestamp:',
      Date.now(),
    );

    const cacheKey = 'continue-watching';

    if (!clearCache) {
      const cached = await playbackCache.get<MediaItem[]>(cacheKey);
      if (cached) {
        logger.debug(`${LP} Returning cached continue watching data - items:`, cached.length);
        return cached;
      }
      logger.debug(`${LP} No cache found, fetching from API`);
    }

    // Fetch continue watching for multiple item types
    const itemTypes = ['movie', 'episode'] as const;

    logger.debug(`${LP} Making API calls for itemTypes:`, itemTypes);
    const responses = await Promise.all(
      itemTypes.map((itemType) => {
        const endpoint = '/continue-watching';
        const queryParams = { itemType };
        return NzosPlaybackService.apiCall<{ items: MediaItem[] }>(endpoint, token, undefined, 'GET', queryParams);
      }),
    );

    const allItems = responses.flatMap((response) => response.items || []);
    logger.debug(`${LP} API calls completed - total items:`, allItems.length);
    await playbackCache.set(cacheKey, allItems, SHORT_TTL);

    return allItems;
  }

  /**
   * Get continue watching data for a specific show.
   * @param params - Parameters.
   * @param params.showId - Show identifier.
   * @param params.token - Auth token.
   * @returns Continue watching show response.
   */
  async getContinueWatchingByShowId({
    showId,
    token,
  }: {
    showId: string;
    token: string;
  }): Promise<ContinueWatchingShowResponse> {
    logger.debug(`${LP} getContinueWatchingByShowId called for showId: ${showId}`);
    logger.warn(`${LP} getContinueWatchingByShowId not directly supported by NZOS API, using continue-watching list`);

    // NZOS doesn't have show-specific endpoint, filter from main list
    const items = await this.getContinueWatchingData({ token, clearCache: false });
    const showItems = items.filter((item) => item.showId === showId);

    if (showItems.length === 0) {
      return {
        tvShowId: showId,
        continueWatchingEpisodeId: '',
        continueWatchingSeasonId: '',
        watched: false,
      };
    }

    // Find the most recent episode based on lastWatchedAt
    const mostRecent = showItems.reduce((prev, current) => {
      const prevTime = prev.watchingProgress?.lastWatchedAt || '';
      const currentTime = current.watchingProgress?.lastWatchedAt || '';
      return currentTime > prevTime ? current : prev;
    }, showItems[0]);

    return {
      tvShowId: showId,
      continueWatchingEpisodeId: mostRecent.assetId || '',
      continueWatchingSeasonId: mostRecent.seasonId || '',
      watched: (mostRecent.watchingProgress?.percentageWatched || 0) >= 95,
    };
  }

  /**
   * Get continue watching data for a specific season.
   * @param params - Parameters.
   * @param params.seasonId - Season identifier.
   * @param params.token - Auth token.
   * @returns List of continue watching items for the season.
   */
  async getContinueWatchingBySeasonId({ seasonId, token }: { seasonId: string; token: string }): Promise<MediaItem[]> {
    logger.debug(`${LP} getContinueWatchingBySeasonId called for seasonId: ${seasonId}`);
    logger.warn(`${LP} getContinueWatchingBySeasonId not directly supported by NZOS API, filtering from list`);

    // NZOS doesn't have season-specific endpoint, filter from main list
    const items = await this.getContinueWatchingData({ token, clearCache: false });
    return items.filter((item) => item.seasonId === seasonId);
  }

  /**
   * Get playback position for a specific asset.
   * @param params - Parameters.
   * @param params.assetId - Asset identifier.
   * @param params.token - Auth token.
   * @returns Play position data.
   */
  // eslint-disable-next-line class-methods-use-this
  async getPlayPositionByAssetId({ assetId, token }: { assetId: string; token: string }): Promise<PlayPosition> {
    logger.debug(`${LP} getPlayPositionByAssetId called for assetId: ${assetId}`);

    const endpoint = `/continue-watching/${assetId}`;
    const response = await NzosPlaybackService.apiCall<PlayPosition>(endpoint, token);

    return response;
  }

  /**
   * Remove asset from continue watching list.
   * @param params - Parameters.
   * @param params.assetId - Asset identifier.
   * @param params.token - Auth token.
   * @returns Promise that resolves when deleted.
   */
  // eslint-disable-next-line class-methods-use-this
  async deleteContinueWatching({ assetId, token }: { assetId: string; token: string }): Promise<void> {
    logger.debug(`${LP} deleteContinueWatching called for assetId: ${assetId}`);

    const endpoint = `/continue-watching/${assetId}`;
    await NzosPlaybackService.apiCall<void>(endpoint, token, undefined, 'DELETE');

    // Invalidate cache after deletion
    const cacheKey = 'continue-watching';
    await playbackCache.remove(cacheKey);
  }

  /**
   * Make playback session API call.
   * @param params - Playback action parameters.
   * @param params.assetId - The asset ID for playback.
   * @param params.token - Authentication token.
   * @returns Playback response.
   */
  private async getPlaybackSession({
    assetId,
    token,
    ...rest
  }: PlaybackActionParams): Promise<PlaybackResponse | null> {
    logger.debug(`${LP} getPlaybackSession called for eventType: ${rest.eventType}`);

    const { deviceId, os = UNKNOWN } = await getDeviceInfo();

    const endpoint = `/playback/${assetId}`;
    // Build request body according to API schema
    const body = {
      platform: 'ctv' as const, // Required
      name: APP_NAME, // Required: Application name
      appVersion: APP_VERSION, // Required
      eventType: rest.eventType, // Required
      device: {
        // Required
        deviceId,
        os,
      },
      // Optional fields
      ...(rest.sessionId && { sessionId: rest.sessionId }),
      ...(rest.position !== undefined && { position: rest.position }),
      ...(APP_NAME && { appName: APP_NAME }), // Optional: Alternative application name field
    };

    const response = await NzosPlaybackService.apiCall<PlaybackResponse>(endpoint, token, body, 'POST');

    if (response.sessionId) {
      this.sessionId = response.sessionId;
    }

    return response;
  }

  /**
   * Reset playback session.
   */
  private async reset(): Promise<void> {
    this.sessionId = undefined;
  }

  /**
   * Initiate playback session.
   * @param args - Playback action parameters.
   * @param args.assetId - The asset ID for playback.
   * @param args.token - Authentication token.
   * @returns Playback response with media URLs.
   */
  async play({ assetId, token }: PlaybackActionHelperProps): Promise<PlaybackResponse | null> {
    logger.debug(`${LP} play called for assetId: ${assetId}`);

    return this.getPlaybackSession({
      eventType: 'play',
      assetId,
      token,
    });
  }

  /**
   * Send pause playback event.
   * @param args - Playback action parameters.
   * @returns Playback response.
   */
  async pause(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null> {
    if (!this.sessionId) {
      logger.warn(`${LP} No sessionId found, cannot send pause playback event`);
      return null;
    }

    logger.debug(`${LP} pause called for assetId: ${args.assetId}`);

    return this.getPlaybackSession({
      ...args,
      eventType: 'pause',
      sessionId: this.sessionId,
    });
  }

  /**
   * Send resume playback event.
   * @param args - Playback action parameters.
   * @returns Playback response.
   */
  async resume(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null> {
    if (!this.sessionId) {
      logger.warn(`${LP} No sessionId found, cannot send resume playback event`);
      return null;
    }

    logger.debug(`${LP} resume called for assetId: ${args.assetId}`);

    return this.getPlaybackSession({
      ...args,
      eventType: 'resume',
      sessionId: this.sessionId,
    });
  }

  /**
   * Send playback progress event.
   * @param args - Playback action parameters.
   * @returns Playback response.
   */
  async progress(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null> {
    if (!this.sessionId) {
      logger.warn(`${LP} No sessionId found, cannot send progress playback event`);
      return null;
    }

    logger.debug(`${LP} progress called for assetId: ${args.assetId}, position: ${args.position}`);

    return this.getPlaybackSession({
      ...args,
      eventType: 'progress',
      sessionId: this.sessionId,
    });
  }

  /**
   * Send stop playback event and end session.
   * @param args - Playback action parameters.
   * @returns Playback response.
   */
  async stop(args: PlaybackActionHelperProps): Promise<PlaybackResponse | null> {
    const { sessionId } = this;

    if (!sessionId) {
      logger.warn(`${LP} No sessionId found, cannot send stop playback event`);
      return null;
    }

    logger.debug(`${LP} stop called for assetId: ${args.assetId}`);

    const response = await this.getPlaybackSession({
      ...args,
      eventType: 'stop',
      sessionId,
    });

    this.reset();

    return response;
  }
}

export { NzosPlaybackService };
